import React from 'react';
import {
  Box,
  Container,
  Grid,
  Link,
  Stack,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Email as EmailIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';

import { brand } from '@penticton-dev-events/shared';
import { useAppTheme } from '../../app/providers/ThemeProvider';
import { getSecondaryNavigationItems } from './NavigationConfig';
import { useAuth } from '../../app/providers/AuthProvider';
import { getBrandGradient, getBrandDivider, brandAssets } from '../../theme/tokens';

const Footer: React.FC = () => {
  const muiTheme = useTheme();
  const { customTokens: tokens } = useAppTheme();
  const { user, profile } = useAuth();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(muiTheme.breakpoints.down('md'));

  const isAuthenticated = Boolean(user);
  const isAdmin = profile?.isAdmin ?? false;
  const secondaryNavItems = getSecondaryNavigationItems(isAuthenticated, isAdmin);

  const currentYear = new Date().getFullYear();

  const footerSections = {
    navigation: {
      title: 'Navigation',
      links: [
        { label: 'Home', path: '/' },
        { label: 'Groups', path: '/groups' },
        { label: 'Events', path: '/meetings' },
        ...secondaryNavItems.map(item => ({ label: item.label, path: item.path })),
      ],
    },
    community: {
      title: 'Community',
      links: [
        { label: 'Join a Group', path: '/groups' },
        { label: 'Community Guidelines', path: '/legal/community-guidelines' },
        { label: 'Volunteer', path: '/about' },
        { label: 'Suggest Event', path: '/groups' },
      ],
    },
    legal: {
      title: 'Legal',
      links: [
        { label: 'Terms of Use', path: '/legal/terms' },
        { label: 'Privacy Policy', path: '/legal/privacy' },
        { label: 'Community Guidelines', path: '/legal/community-guidelines' },
      ],
    },
  };

  return (
    <Box
      component="footer"
      sx={{
        background: getBrandGradient('subtle', muiTheme.palette.mode as 'light' | 'dark'),
        borderTop: `2px solid ${getBrandDivider('brand', muiTheme.palette.mode as 'light' | 'dark')}`,
        mt: 'auto',
        pt: tokens.spacing[8],
        pb: tokens.spacing[4],
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '1px',
          background: getBrandGradient(
            'accent',
            muiTheme.palette.mode as 'light' | 'dark',
          ),
          opacity: 0.3,
        },
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={isTablet ? 4 : 6}>
          {/* Brand Section */}
          <Grid size={{ xs: 12, md: 3 }}>
            <Stack spacing={3}>
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: tokens.typography.fontWeight.bold,
                    background: getBrandGradient(
                      'primary',
                      muiTheme.palette.mode as 'light' | 'dark',
                    ),
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: tokens.spacing[1],
                  }}
                >
                  {brand.organizer.name}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: tokens.spacing[2] }}
                >
                  {brand.messaging.tagline}
                </Typography>

                {/* Contact Info */}
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {brand.location.city}, {brand.location.province},{' '}
                      {brand.location.country}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmailIcon fontSize="small" color="action" />
                    <Link
                      href={`mailto:${brand.contact.email}`}
                      color="text.secondary"
                      underline="hover"
                      variant="body2"
                    >
                      {brand.contact.email}
                    </Link>
                  </Box>
                </Stack>
              </Box>
            </Stack>
          </Grid>

          {/* Navigation Links */}
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: tokens.typography.fontWeight.semibold,
                mb: tokens.spacing[3],
                color: 'text.primary',
              }}
            >
              {footerSections.navigation.title}
            </Typography>
            <Stack spacing={2}>
              {footerSections.navigation.links.map(link => (
                <Link
                  key={link.path}
                  component={RouterLink}
                  to={link.path}
                  color="text.secondary"
                  underline="hover"
                  variant="body2"
                  sx={{
                    transition: `color ${tokens.motion.duration.fast} ${tokens.motion.easing.ease}`,
                    '&:hover': {
                      color: 'primary.main',
                    },
                  }}
                >
                  {link.label}
                </Link>
              ))}
            </Stack>
          </Grid>

          {/* Community Links */}
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: tokens.typography.fontWeight.semibold,
                mb: tokens.spacing[3],
                color: 'text.primary',
              }}
            >
              {footerSections.community.title}
            </Typography>
            <Stack spacing={2}>
              {footerSections.community.links.map(link => (
                <Link
                  key={link.label}
                  {...((link as any).href
                    ? { href: (link as any).href }
                    : { component: RouterLink, to: link.path })}
                  color="text.secondary"
                  underline="hover"
                  variant="body2"
                  sx={{
                    transition: `color ${tokens.motion.duration.fast} ${tokens.motion.easing.ease}`,
                    '&:hover': {
                      color: 'primary.main',
                    },
                  }}
                >
                  {link.label}
                </Link>
              ))}
            </Stack>
          </Grid>

          {/* Legal Links */}
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: tokens.typography.fontWeight.semibold,
                mb: tokens.spacing[3],
                color: 'text.primary',
              }}
            >
              {footerSections.legal.title}
            </Typography>
            <Stack spacing={2}>
              {footerSections.legal.links.map(link => (
                <Link
                  key={link.path}
                  component={RouterLink}
                  to={link.path}
                  color="text.secondary"
                  underline="hover"
                  variant="body2"
                  sx={{
                    transition: `color ${tokens.motion.duration.fast} ${tokens.motion.easing.ease}`,
                    '&:hover': {
                      color: 'primary.main',
                    },
                  }}
                >
                  {link.label}
                </Link>
              ))}
            </Stack>
          </Grid>
        </Grid>

        {/* Copyright */}
        <Box
          sx={{
            borderTop: `1px solid ${getBrandDivider('accent', muiTheme.palette.mode as 'light' | 'dark')}`,
            mt: tokens.spacing[6],
            pt: tokens.spacing[4],
            textAlign: 'center',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            © {currentYear} Penticton.dev Events - Built by the community, for the
            community
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
